import { useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import {
  MODERATION_BLOCKED_REASON,
  MODERATION_UNAVAILABLE_REASON,
  type MediaRef,
  type UserDoc,
} from '@astrally/shared';
import { MediaError, prepareAvatar, type PreparedImage } from '../../lib/media';
import { getFirebase, isDemoMode } from '../../lib/firebase';
import { useApp } from '../../state/AppState';
import type { Backend } from '../../lib/backend/types';
import { Avatar, appStorage } from '../../components/common';

/**
 * THE PROFILE PICTURE — pick or capture, resize on the device, look at it,
 * then commit.
 *
 * COST is why this component exists rather than a plain file input. Discovery
 * renders dozens of these per session and egress, not storage, is the bill: at
 * 10k people browsing 20 cards a day every extra 10KB of avatar is roughly a
 * rupee a day, forever. `prepareAvatar` therefore re-encodes to 256px WebP
 * under 30KB before a byte leaves the phone (AVATAR_MAX_PX / AVATAR_MAX_BYTES),
 * and the *server* caps the object at the same number in storage.rules, which
 * is the one of the two that is actually enforcement.
 *
 * ORDER, and it is the whole point of the flow:
 *   prepare → REVIEW → grant → upload → moderate → hand the ref to the parent.
 * Nothing is uploaded before the user has seen what the resize produced, and
 * nothing becomes an avatar before moderation has run. A face is the single
 * most visible thing about an account — it is on every card, in every request,
 * beside every message — so it goes through the same check as a posted image
 * rather than a laxer one.
 *
 * A refused image is deleted by the moderation callable, so blocked content is
 * never persisted. If the call fails for any other reason this component
 * deletes the object itself, so a failed attempt does not leave a paid-for
 * orphan in the bucket.
 *
 * TODAY THAT CHECK IS INERT AND WE DO NOT PRETEND OTHERWISE. The moderation
 * provider has no `checkImage` yet (functions/src/media/mediaFns.ts spells out
 * the three-line patch), so every upload is honestly recorded `unchecked`
 * rather than `clean`. This component's job is to make the call at the right
 * moment and respect its verdict; when the provider grows the method, nothing
 * here changes.
 */

/** What `mediaGrantUpload` hands back. Mirrors MediaUploadGrant on the server. */
interface UploadGrant {
  path: string;
  contentType: string;
  cacheControl: string;
  maxBytes: number;
}

export interface AvatarPickerProps {
  /** The account being edited — supplies the current picture and the initials. */
  user: UserDoc;
  /**
   * The saved document. The picker persists the change itself (one component
   * owns "the picture is now this"); screens keep their own copy of the user
   * in local state, so they need the result rather than a signal.
   */
  onSaved: (user: UserDoc) => void;
  /** One line under the buttons — each screen explains the requirement itself. */
  hint?: string;
  size?: number;
}

export function AvatarPicker({ user, onSaved, hint, size = 72 }: AvatarPickerProps) {
  const app = useApp();
  const name = user.displayName;
  const value = user.avatar;
  const [pending, setPending] = useState<PreparedImage | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState<'' | 'preparing' | 'saving' | 'removing'>('');
  const [error, setError] = useState('');
  const chooseRef = useRef<HTMLInputElement>(null);
  const captureRef = useRef<HTMLInputElement>(null);

  // The review preview is a local object URL over the RESIZED blob — what the
  // user approves is exactly what gets uploaded, not the original they picked.
  useEffect(() => {
    if (!pending) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(pending.blob);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pending]);

  const pick = async (file: File | undefined) => {
    if (!file) return;
    setError('');
    setBusy('preparing');
    try {
      setPending(await prepareAvatar(file));
    } catch (e) {
      setPending(null);
      setError(refusalMessage(e));
    } finally {
      setBusy('');
    }
  };

  const confirm = async () => {
    if (!pending) return;
    setBusy('saving');
    setError('');
    try {
      const next = await uploadAvatar(pending);
      onSaved(await persistAvatar(app.backend, user, next));
      setPending(null);
    } catch (e) {
      setError(refusalMessage(e));
    } finally {
      setBusy('');
    }
  };

  const remove = async () => {
    setBusy('removing');
    setError('');
    try {
      onSaved(await persistAvatar(app.backend, user, null));
    } catch (e) {
      setError(refusalMessage(e));
    } finally {
      setBusy('');
    }
  };

  const working = busy !== '';
  const shown = pending ? previewUrl ?? undefined : value?.path;

  return (
    <div className="col" style={{ gap: 10 }}>
      <div className="row" style={{ alignItems: 'flex-start' }}>
        <Avatar name={name || '?'} path={shown} size={size} gold />

        <div className="col" style={{ flex: 1, gap: 8 }}>
          {pending ? (
            <>
              <div className="tiny muted">
                Resized to {pending.width}×{pending.height}, {Math.round(pending.bytes / 1000)}KB.
                Small on purpose — this is what everyone will see on your card.
              </div>
              <div className="row" style={{ flexWrap: 'wrap' }}>
                <button className="btn btn--primary btn--small" disabled={working} onClick={confirm}>
                  {busy === 'saving' ? <span className="spinner" /> : 'Use this photo'}
                </button>
                <button
                  className="btn btn--ghost btn--small"
                  disabled={working}
                  onClick={() => chooseRef.current?.click()}
                >
                  Pick another
                </button>
                <button
                  className="btn btn--ghost btn--small"
                  disabled={working}
                  onClick={() => {
                    setPending(null);
                    setError('');
                  }}
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <div className="row" style={{ flexWrap: 'wrap' }}>
              <button
                className="btn btn--primary btn--small"
                disabled={working}
                onClick={() => chooseRef.current?.click()}
              >
                {busy === 'preparing' ? (
                  <span className="spinner" />
                ) : value ? (
                  'Change photo'
                ) : (
                  'Add a photo'
                )}
              </button>
              <button
                className="btn btn--ghost btn--small"
                disabled={working}
                onClick={() => captureRef.current?.click()}
              >
                Take one
              </button>
              {value && (
                <button className="btn btn--ghost btn--small" disabled={working} onClick={remove}>
                  {busy === 'removing' ? <span className="spinner" /> : 'Remove'}
                </button>
              )}
            </div>
          )}

          {busy === 'saving' && (
            <div className="tiny faint" role="status">
              Uploading and checking your photo…
            </div>
          )}

          {hint && !pending && (
            <div className="tiny faint" style={{ lineHeight: 1.5 }}>
              {hint}
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="banner banner--danger small" role="alert" style={{ lineHeight: 1.5 }}>
          {error}
        </div>
      )}

      {/* Two inputs rather than one: `capture` asks the OS for the camera
          directly, and a picker that only ever opens the gallery is a poor
          answer to "add a photo of yourself" on a phone. Both are hidden and
          driven by the visible buttons above, which carry the real labels. */}
      <input
        ref={chooseRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          void pick(e.target.files?.[0]);
          // Clearing lets the same file be chosen twice in a row — otherwise
          // "pick another", change your mind, pick the same one fires nothing.
          e.target.value = '';
        }}
      />
      <input
        ref={captureRef}
        type="file"
        accept="image/*"
        capture="user"
        hidden
        onChange={(e) => {
          void pick(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Upload and save
// ---------------------------------------------------------------------------

function withoutAvatar(user: UserDoc): UserDoc {
  const next = { ...user };
  delete next.avatar;
  return next;
}

/**
 * Save the picture onto the account.
 *
 * NOT `saveUser`, which is a direct Firestore write. `avatar` is a pointer at a
 * Storage object, and two things about that pointer cannot be checked by a
 * Firestore rule: that the object belongs to this account (avatars are readable
 * by any signed-in user, so a free-form write would let someone wear another
 * member's face), and that moderation actually ran on it. The `avatarSet`
 * callable checks both, deletes the object the account used to point at, and
 * rebuilds the discovery index — without that last step the new picture would
 * appear on no card at all.
 */
async function persistAvatar(
  backend: Backend,
  user: UserDoc,
  next: MediaRef | null,
): Promise<UserDoc> {
  const updated = next ? { ...user, avatar: next } : withoutAvatar(user);
  if (backend.kind === 'demo') {
    // No project, no callable, no bucket: the demo backend rewrites the whole
    // document, so dropping the key is how a picture is removed there.
    await backend.saveUser(updated);
    return updated;
  }
  await httpsCallable<{ avatar: MediaRef | null }, unknown>(
    getFirebase().fns,
    'avatarSet',
  )({ avatar: next });
  return updated;
}

/**
 * grant → upload → moderate. The server chooses the path, so a client cannot
 * decide where its object lands, and the id is a UUID: between the upload and
 * the moderation call the object exists at a path that appears in no document
 * and nobody can guess.
 */
async function uploadAvatar(prepared: PreparedImage): Promise<MediaRef> {
  const ref: MediaRef = {
    path: '',
    kind: 'image',
    width: prepared.width,
    height: prepared.height,
    bytes: prepared.bytes,
    blurhash: prepared.blurhash,
  };

  // Demo mode has no project and no bucket. The picture IS the data URL — the
  // whole demo profile already lives in IndexedDB, and at under 30KB this is
  // smaller than most of what is stored beside it. Everything downstream
  // renders it unchanged because the resolver in common.tsx treats a `data:`
  // path as already renderable.
  if (isDemoMode()) {
    return { ...ref, path: await blobToDataUrl(prepared.blob) };
  }

  const { fns } = getFirebase();
  const grant = (
    await httpsCallable<Record<string, unknown>, UploadGrant>(fns, 'mediaGrantUpload')({
      target: 'avatar',
      kind: 'image',
      contentType: prepared.contentType,
      bytes: prepared.bytes,
    })
  ).data;

  const [{ ref: storageRef, uploadBytes }, storage] = await Promise.all([
    import('firebase/storage'),
    appStorage(),
  ]);
  await uploadBytes(storageRef(storage, grant.path), prepared.blob, {
    contentType: grant.contentType,
    // Storage rules cannot inspect Cache-Control, so forgetting this is silent
    // and permanent: the object is write-once, so a picture uploaded without it
    // is re-fetched in full for its whole life. The grant returns it for
    // exactly that reason.
    cacheControl: grant.cacheControl,
  });

  try {
    await httpsCallable<{ path: string }, unknown>(fns, 'mediaModerateImage')({ path: grant.path });
  } catch (e) {
    // A refusal already deleted the object server-side; this covers everything
    // else (a dropped connection, a timeout) so a failed attempt does not leave
    // an unreferenced object nobody will ever look at but everybody pays to
    // store.
    await deleteObject(grant.path);
    throw e;
  }

  return { ...ref, path: grant.path };
}

/** Best-effort. An orphaned object is a cost, not a correctness problem. */
async function deleteObject(path: string): Promise<void> {
  if (isDemoMode() || /^data:/.test(path)) return;
  try {
    const [{ ref: storageRef, deleteObject: remove }, storage] = await Promise.all([
      import('firebase/storage'),
      appStorage(),
    ]);
    await remove(storageRef(storage, path));
  } catch {
    // Already gone, or offline. Either way the document no longer points here.
  }
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Could not read that image.'));
    reader.readAsDataURL(blob);
  });
}

/**
 * One sentence the user can act on.
 *
 * `MediaError.message` is already specific and written for a person ("that clip
 * is 22MB; the limit is 6MB") so it is shown as-is. Callable failures are keyed
 * off `details.reason`, never off the message text — that text is user-facing
 * copy that will be translated, and matching on it would break silently the
 * day it is.
 */
function refusalMessage(e: unknown): string {
  if (e instanceof MediaError) return e.message;
  const reason = (e as { details?: { reason?: unknown } } | null)?.details?.reason;
  if (reason === MODERATION_BLOCKED_REASON) {
    return 'That photo can’t be used as a profile picture — it looks like it breaks our community rules. Try a different one.';
  }
  if (reason === MODERATION_UNAVAILABLE_REASON) {
    return 'We couldn’t check that photo just now, so it wasn’t saved. Try again in a moment.';
  }
  return String(e)
    .replace(/^(FirebaseError|Error):\s*/, '')
    .slice(0, 220);
}
